// §7.8 — the Trash.
//
// `remove()` never deletes. It **moves** the file to `/Users/<user>/.Trash/<original-path-
// encoded>` and drops a sidecar recording where it came from and when. Restore puts it back at
// exactly that path, creating parents. Emptying the Trash is the only destructive operation the
// VFS has, and it always asks first, through the shell-drawn dialog of §19.4 — the one surface an
// app cannot draw over or fake.
//
// Because the move goes through the ordinary tree, a file's version history follows it in and
// back out again for free (§7.5).
import {
  basename,
  extname,
  joinVfs,
  normalizeVfsPath,
  trashRoot,
  type FileEntry,
  type VfsPath,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { OpfsError } from './opfs';
import type { VfsTree } from './tree';

/** Sidecars live in a subdirectory an encoded key can never name — every key starts `%2F`. */
export const TRASH_META_DIR = '.meta';

export interface TrashEntry {
  /** The name under `.Trash`: `encodeURIComponent` of the original path (§7.8). */
  key: string;
  /** Where it sits now. */
  path: VfsPath;
  /** Where it came from, and where `restore` puts it back. */
  originalPath: VfsPath;
  /** The original basename, for display. */
  name: string;
  isDir: boolean;
  size: number;
  deletedAt: number;
}

export interface TrashService {
  /** Move `path` to the Trash. This is what `HangarAPI.fs.remove` does (§4.1). */
  trash(path: string): Promise<TrashEntry>;
  /** Newest first. */
  list(): Promise<TrashEntry[]>;
  /** Back to the original path, creating parents. Returns where it actually landed. */
  restore(key: string): Promise<FileEntry>;
  /** Permanently delete one item. Confirms first (§19.4); `false` means the user said no. */
  deleteForever(key: string): Promise<boolean>;
  /** The only destructive operation in the VFS. Always confirms (§7.8, §19.4). */
  empty(): Promise<boolean>;
  /** For the Recycle Bin icon and the Settings readout. */
  usage(): Promise<{ items: number; bytes: number }>;
}

export interface TrashOptions {
  files: VfsTree;
  /** `shellui.confirm` — the shell-drawn dialog of §19.4, never `window.confirm`. */
  confirm: (message: string) => Promise<boolean>;
  user: () => string;
  now?: () => number;
  /** Purging an item should take its version history with it (§7.5). */
  forgetHistory?: (path: VfsPath) => Promise<void>;
}

export class TrashEntryNotFoundError extends Error {
  readonly code = 'NOT_FOUND' as const;
  readonly key: string;

  constructor(key: string) {
    super(`trash: no item ${JSON.stringify(key)}`);
    this.name = 'TrashEntryNotFoundError';
    this.key = key;
  }
}

interface Sidecar {
  version: 1;
  originalPath: VfsPath;
  name: string;
  isDir: boolean;
  size: number;
  deletedAt: number;
}

/** §7.8's `<original-path-encoded>`. Injective, so two different paths never share a key. */
export function encodeTrashKey(path: VfsPath): string {
  return encodeURIComponent(path);
}

export function decodeTrashKey(key: string): VfsPath | null {
  let decoded: string;
  try {
    decoded = decodeURIComponent(key.replace(/-\d+$/, ''));
  } catch {
    return null;
  }
  const parsed = normalizeVfsPath(decoded);
  return parsed.ok ? parsed.path : null;
}

function isSidecar(value: unknown): value is Sidecar {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.originalPath === 'string' &&
    typeof record.name === 'string' &&
    typeof record.isDir === 'boolean' &&
    typeof record.size === 'number' &&
    typeof record.deletedAt === 'number'
  );
}

export function createTrashService(options: TrashOptions): TrashService {
  const { files, confirm } = options;
  const now = options.now ?? (() => Date.now());

  const root = (): VfsPath => trashRoot(options.user());
  const metaDir = (): VfsPath => joinVfs(root(), TRASH_META_DIR);
  const itemPath = (key: string): VfsPath => joinVfs(root(), key);
  const sidecarPath = (key: string): VfsPath => joinVfs(metaDir(), `${key}.json`);

  function requirePath(input: string): VfsPath {
    const parsed = normalizeVfsPath(input);
    if (!parsed.ok) throw new OpfsError(parsed.error, input);
    return parsed.path;
  }

  /** A free name for `key` — deleting the same path twice must not clobber the first copy. */
  async function freeKey(base: string): Promise<string> {
    if (!(await files.exists(itemPath(base)))) return base;
    for (let n = 2; ; n += 1) {
      const candidate = `${base}-${n}`;
      if (!(await files.exists(itemPath(candidate)))) return candidate;
    }
  }

  /**
   * A free path for a restore. §7.8 says "returns it to its original path", but if something is
   * there now, overwriting it would make restore destructive — the one thing the Trash exists to
   * prevent. Disambiguating follows D-045's rule that nothing is ever silently overwritten.
   */
  async function freePath(path: VfsPath): Promise<VfsPath> {
    if (!(await files.exists(path))) return path;
    const extension = extname(path);
    const stem = basename(path).slice(0, basename(path).length - extension.length);
    const parent = path.slice(0, path.length - basename(path).length - 1) || '/';
    for (let n = 1; ; n += 1) {
      const suffix = n === 1 ? ' (restored)' : ` (restored ${n})`;
      const candidate = joinVfs(parent, `${stem}${suffix}${extension}`);
      if (!(await files.exists(candidate))) return candidate;
    }
  }

  async function readSidecar(key: string): Promise<Sidecar | null> {
    try {
      const parsed: unknown = JSON.parse(await files.readText(sidecarPath(key)));
      return isSidecar(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Everything the sidecar would have said, recovered from the key when it is missing. */
  async function entryFor(key: string, size: number, isDir: boolean): Promise<TrashEntry | null> {
    const sidecar = await readSidecar(key);
    const originalPath = sidecar?.originalPath ?? decodeTrashKey(key);
    if (originalPath == null) return null;
    return {
      key,
      path: itemPath(key),
      originalPath,
      name: sidecar?.name ?? basename(originalPath),
      isDir: sidecar?.isDir ?? isDir,
      size: sidecar?.size ?? size,
      deletedAt: sidecar?.deletedAt ?? 0,
    };
  }

  async function purge(entry: TrashEntry): Promise<void> {
    await files.remove(entry.path, { recursive: true });
    if (await files.exists(sidecarPath(entry.key))) await files.remove(sidecarPath(entry.key));
    if (options.forgetHistory) await options.forgetHistory(entry.path);
  }

  return {
    async trash(path) {
      const source = requirePath(path);
      const stat = await files.stat(source);
      if (!stat) throw new OpfsError('NOT_FOUND', source);

      const key = await freeKey(encodeTrashKey(source));
      await files.move(source, itemPath(key));

      const sidecar: Sidecar = {
        version: 1,
        originalPath: source,
        name: stat.name,
        isDir: stat.isDir,
        size: stat.size,
        deletedAt: now(),
      };
      await files.write(sidecarPath(key), JSON.stringify(sidecar));

      return {
        key,
        path: itemPath(key),
        originalPath: source,
        name: stat.name,
        isDir: stat.isDir,
        size: stat.size,
        deletedAt: sidecar.deletedAt,
      };
    },

    async list() {
      let contents;
      try {
        contents = await files.list(root());
      } catch {
        return []; // no Trash directory yet
      }
      const entries: TrashEntry[] = [];
      for (const item of contents) {
        if (item.name === TRASH_META_DIR) continue;
        const entry = await entryFor(item.name, item.size, item.isDir);
        if (entry) entries.push(entry);
      }
      return entries.sort((a, b) => b.deletedAt - a.deletedAt);
    },

    async restore(key) {
      const stat = await files.stat(itemPath(key));
      if (!stat) throw new TrashEntryNotFoundError(key);
      const entry = await entryFor(key, stat.size, stat.isDir);
      if (!entry) throw new TrashEntryNotFoundError(key);

      const destination = await freePath(entry.originalPath);
      const restored = await files.move(entry.path, destination);
      if (await files.exists(sidecarPath(key))) await files.remove(sidecarPath(key));
      return restored;
    },

    async deleteForever(key) {
      const stat = await files.stat(itemPath(key));
      if (!stat) throw new TrashEntryNotFoundError(key);
      const entry = await entryFor(key, stat.size, stat.isDir);
      if (!entry) throw new TrashEntryNotFoundError(key);

      if (!(await confirm(COPY.deleteForever(entry.name)))) return false;
      await purge(entry);
      return true;
    },

    async empty() {
      const entries = await this.list();
      if (entries.length === 0) return true;
      if (!(await confirm(COPY.emptyTrash(entries.length)))) return false;
      for (const entry of entries) await purge(entry);
      return true;
    },

    async usage() {
      const entries = await this.list();
      return {
        items: entries.length,
        bytes: entries.reduce((total, entry) => total + entry.size, 0),
      };
    },
  };
}
